import { useCallback } from 'react'
import { stopPerformanceMeasure, trackUserEvent } from '@vend/utilities'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  finalisingSale,
  finalisingSaleComplete,
} from '../../../state/slices/uiStateSlice'
import {
  usePaymentManager,
  useRegisterManager,
  useSaleManager,
  useTrainingModeManager,
  useUserSwitchingService,
} from '../../../ng-compat/hooks'
import {
  ReduxCurrentSale,
  selectCurrentSale,
  selectLineItems,
  setSaleCompletedBy,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  isMarkedAsUnfulfilled,
  isQuote,
  isUploadedToBeCompleted,
} from '../../../functions/sale'
import { WEB_REGISTER } from '../../web-register.constant'
import { SaleStatus, SaleSyncStatus } from '../../../models/sale.constants'
import { STATES } from '../../../../../lib/routes'
import { SaleManager } from '../../../managers/sale-manager'
import { $state } from '../../../ng-compat/imports'
import { LineItem } from '../../../models/line-item'
import { PaymentManager } from '../../../managers/payment-manager'
import { clearPaymentRequestSuccess } from '../../../state/slices/posState/posStateSlice'
import { useSpecialOrderProduct } from '../../special-order-product/useSpecialOrderProduct'
import { useSpecialOrderProductToast } from '../../special-order-product/useSpecialOrderProductToast'
import { store } from '../../../state/store'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useSpecialOrderUBE } from '../../special-order-product/useSpecialOrderUBE'
import { usePrintReceipt } from './usePrintReceipt'

const trackAccountSaleEvent = ({
  sale,
  lineItems,
}: {
  sale: ReduxCurrentSale
  lineItems: LineItem[]
}) => {
  trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Close Sale Confirmation', {
    id: sale.localId,
  })

  if (
    sale.status === SaleStatus.ACCOUNT_SALE ||
    sale.status === SaleStatus.ACCOUNT_SALE_CLOSED
  ) {
    const existingLineItemCount = lineItems.filter(lineItem =>
      Boolean(lineItem.id)
    ).length

    if (existingLineItemCount > 0 && existingLineItemCount < lineItems.length) {
      trackUserEvent(
        WEB_REGISTER.APPLICATION_NAME,
        'Added additional product(s) to on-account sale'
      )
    }
  }
}

const getPayments = (sale: ReduxCurrentSale) =>
  sale.payments.map(p => {
    return {
      paymentTypeName: p.paymentType.name,
      paymentTypeId: p.paymentType.type_id,
      paymentDate: p.paymentDate,
      retailerPaymentTypeId: p.paymentType.id,
      salePaymentId: p.localId,
    }
  })

const clearPaymentMessages = (paymentManager: PaymentManager) => {
  paymentManager.paymentIntegrationReceiptMessage = null
  paymentManager.acceptedPaymentIntegrationReceiptMessages = []
}

// Timeout for waiting for early submit to complete (in milliseconds).
// If the network is down, we don't want to block forever - fall back to local invoice number.
const EARLY_SUBMIT_TIMEOUT_MS = 5000

// Timeout for waiting for receipt templates to load (in milliseconds).
const RECEIPT_LOAD_TIMEOUT_MS = 5000

/**
 * Wait for the early submit sale to complete, with a timeout.
 * This ensures the server-returned invoice number is available before printing,
 * but doesn't block indefinitely if the network is down.
 *
 * @returns true if the early submit completed, false if it timed out
 */
const waitForEarlySubmitComplete = (
  saleManager: SaleManager
): Promise<boolean> =>
  new Promise(resolve => {
    const currentSyncStatus = selectCurrentSale(store.getState()).syncStatus
    if (currentSyncStatus !== SaleSyncStatus.UPLOAD_FIRST_SUBMIT) {
      resolve(true) // Already completed
      return
    }

    const timeoutId = setTimeout(() => {
      saleManager.off('earlySubmitSaleComplete', handler)
      resolve(false) // Timed out
    }, EARLY_SUBMIT_TIMEOUT_MS)

    const handler = () => {
      clearTimeout(timeoutId)
      saleManager.off('earlySubmitSaleComplete', handler)
      resolve(true) // Completed successfully
    }

    saleManager.on('earlySubmitSaleComplete', handler)
  })

/**
 * Wait for receipt templates to load before sending email.
 * This prevents the race condition where email fails because receiptID is not yet available.
 *
 * @returns true if receiptID is available, false if it timed out
 */
const waitForReceiptTemplates = (): Promise<boolean> =>
  new Promise(resolve => {
    const checkReceipt = () => {
      const currentSale = selectCurrentSale(store.getState())
      return !!(currentSale.receiptID && currentSale.receiptEmailAddress)
    }

    // Check immediately - receipts might already be loaded
    if (checkReceipt()) {
      resolve(true)
      return
    }

    const startTime = Date.now()
    const pollInterval = 100 // Check every 100ms

    const intervalId = setInterval(() => {
      if (checkReceipt()) {
        clearInterval(intervalId)
        resolve(true)
      } else if (Date.now() - startTime >= RECEIPT_LOAD_TIMEOUT_MS) {
        clearInterval(intervalId)
        resolve(false)
      }
    }, pollInterval)
  })

export const useCloseSaleConfirmation = () => {
  const dispatch = useAppDispatch()
  const sale = useAppSelector(selectCurrentSale)
  const lineItems = useAppSelector(selectLineItems)
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const trainingModeManager = useTrainingModeManager()
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const userSwitchingService = useUserSwitchingService()
  const printReceipt = usePrintReceipt()
  const isSpecialOrderProduct = useSpecialOrderProduct()
  const specialOrderProductToast = useSpecialOrderProductToast()
  const trackSpecialOrderEvent = useSpecialOrderUBE()

  // Feature flag to enable waiting for receipt templates (defaults to OFF).
  // Only enable for merchants experiencing race condition where receiptID is not
  // available in time for email sending (particularly for payment link sales).
  const shouldWaitForReceiptTemplates = getFeatureFromCache(
    'wait_for_receipt_templates'
  )

  const closeSaleConfirmation = useCallback(
    async (source: string) => {
      dispatch(finalisingSale())

      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Close Sale Confirmation', {
        id: sale.localId,
      })

      if (
        sale.status === SaleStatus.ACCOUNT_SALE ||
        SaleStatus.ACCOUNT_SALE_CLOSED
      ) {
        trackAccountSaleEvent({ sale, lineItems })
      }

      const tempPayments = getPayments(sale)

      dispatch(setSaleCompletedBy(source))

      if (isMarkedAsUnfulfilled(sale.status)) {
        await saleManager.setPickableState()
      }

      // If the sale is still being uploaded for the first time, wait for it to complete (with timeout).
      // This ensures the server-returned invoice number is available for printing.
      // If the network is down, we proceed after the timeout with the local invoice number.
      await waitForEarlySubmitComplete(saleManager)

      // Re-read sale state after potential early submit completion to get the updated syncStatus.
      // The original `sale` variable may be stale if we waited for the early submit to complete.
      let currentSale = selectCurrentSale(store.getState())

      // If email receipt is configured and receiptID is not available, check if we need to wait.
      // The wait logic is feature-flagged (OFF by default) to avoid blocking sale completion
      // for merchants who don't experience the receipt template race condition.
      if (
        shouldWaitForReceiptTemplates &&
        register.email_receipt &&
        currentSale.receiptEmailAddress &&
        !currentSale.receiptID
      ) {
        // Feature flag enabled: wait for receipt templates to load
        const receiptsLoaded = await waitForReceiptTemplates()

        if (!receiptsLoaded) {
          // Timeout occurred - receipts didn't load in time
          // Email won't be sent automatically, but user can resend from sale history
          trackUserEvent(
            WEB_REGISTER.DISPLAY_NAME,
            'Receipt templates timeout',
            {
              saleId: currentSale.localId,
              timeout: RECEIPT_LOAD_TIMEOUT_MS,
            }
          )
        }

        // Re-read state after waiting for receipts
        currentSale = selectCurrentSale(store.getState())
      }

      // Finalize the sale but skip the automatic newSale() call so we can print with full sale data
      if (isUploadedToBeCompleted(currentSale)) {
        await saleManager.completeSale({ skipNewSale: true })
      } else {
        await saleManager.finaliseSale({ skipNewSale: true })
      }

      // Print the receipt while sale data is still available
      const shouldPrint = register.print_receipt && !isQuote(sale.status)
      if (shouldPrint) {
        await printReceipt()
      }

      // line items that are special order products
      const specialOrderProductLineItems = lineItems.filter(lineItem =>
        isSpecialOrderProduct(lineItem)
      )
      const numOfSpecialOrderProducts = specialOrderProductLineItems.length

      /**
       * Send UBE event for special order when not in training mode
       */
      if (!trainingModeManager.trainingModeEnabled) {
        trackSpecialOrderEvent()
      }

      // Now explicitly call newSale() to clear the sale and start fresh
      saleManager.newSale()

      clearPaymentMessages(paymentManager)

      // Clear payment request success flag when leaving confirmation screen
      dispatch(clearPaymentRequestSuccess())

      $state.go(STATES.SALE, {}, { location: 'replace' })

      if (!trainingModeManager.trainingModeEnabled) {
        stopPerformanceMeasure('sale-process', {
          moduleName: 'Web Register',
          metadata: { payments: tempPayments },
        })
      }

      const shouldTaintSession = !isQuote(sale.status)
      await userSwitchingService.switchUserIfNeeded(shouldTaintSession)

      dispatch(finalisingSaleComplete())

      // show the success toast for special order product when sale is completed
      specialOrderProductToast(numOfSpecialOrderProducts)
    },
    [
      dispatch,
      lineItems,
      paymentManager,
      printReceipt,
      register.email_receipt,
      register.print_receipt,
      sale,
      saleManager,
      shouldWaitForReceiptTemplates,
      trainingModeManager.trainingModeEnabled,
      userSwitchingService,
      isSpecialOrderProduct,
      specialOrderProductToast,
      trackSpecialOrderEvent,
    ]
  )

  return closeSaleConfirmation
}
